import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/internal/Observable';
import { map } from 'rxjs/operators';
import { Category } from '../entities/category';
import { SizeType } from '../entities/enums/size-type';
import { Photo } from '../entities/photo';
import { GetOverviewResponse } from '../entities/get-overview-response';
import { Blog } from '../entities/blog';
import { BlogDetail } from '../entities/blog-detail';
import { FileObject } from '../entities/file-object';
import { SimpleAdsSurface } from '../entities/simple-ads-surface';
import { RequirementFromWeb } from '../entities/requirement-from-web';
import { UserDetail } from '../entities/user-detail';
import { AdsSurface } from '../entities/ads-surface';
import { AdsSurfaceType } from '../entities/ads-surface-type';
import { AdsSurfaceTypeEnum } from '../entities/enums/ads-surface-type-enum';
import { AdsSurfaceInfo } from '../entities/ads-surface-info';

@Injectable({
  providedIn: 'root'
})
export class ApiBackendService {

  private baseUrl: string = '/rest';

  constructor(
    private http: HttpClient,
  ) {
  }

  headers: HttpHeaders = new HttpHeaders({ 'Authorization': 'Basic ' + btoa('thorWeb:t1h2o3r4Web') });

  getSurfaces(adsSurfaceTypeIds: AdsSurfaceTypeEnum[]): Observable<AdsSurface[]> {
    const params: HttpParams = new HttpParams().append('adssurfaceTypeId', adsSurfaceTypeIds.toString()).append('adssurfaceStatusId', '2');

    return this.http.get(
      `${this.baseUrl}/adssurfaces`, {
        headers: this.headers,
        params: params
      }
    ).pipe(map(response => response as AdsSurface[]));
  }

  getCodeListSurfaceType(): Observable<AdsSurfaceType[]> {
    return this.http.get(
      `${this.baseUrl}/codelist/adssurfaceTypes`, {
        headers: this.headers
      }
    ).pipe(map(response => response as AdsSurfaceType[]));
  }

  createRequirement(requirementFromWeb: RequirementFromWeb): Observable<boolean> {
    return this.http.post(
      `${this.baseUrl}/adssurfaces/createRequirementFromWeb`,
      requirementFromWeb, {
        headers: this.headers,
      }
    ).pipe(map(response => response as boolean));
  }

  getSurfaceDetail(adsSurfaceId: number): Observable<AdsSurfaceInfo[]> {
    return this.http.get(
      `${this.baseUrl}/adssurfaces/getAdsSurfaceInfo/${adsSurfaceId}`,
      {
        headers: this.headers
      }
    ).pipe(map(response => response as AdsSurfaceInfo[]));
  }

  sendEmailMessage(requestMail): Observable<any> {
    const req = { headers: new HttpHeaders({ 'Authorization': 'Basic ' + btoa('thorWeb:t1h2o3r4Web') }) };
    return this.http.post('/rest/sendEmail', requestMail, req).pipe(map(response => <any>response));
  }

  validateToken(requestToken): Observable<any> {
    const req = {
      headers: new HttpHeaders({ 'Authorization': 'Basic ' + btoa('thorWeb:t1h2o3r4Web') }),
      params: requestToken
    };
    return this.http.get('/rest/adssurfaces/captcha', req).pipe(map(response => <any>response));
  }

  getCategories(categoryStyleId?: number[], categoryStatusIds?: number[], fullCategoryFlag: boolean = true): Observable<Category[]> {
    const params = {};
    if (categoryStyleId) {
      params['categoryStyleId'] = categoryStyleId.join(',');
    }
    if (categoryStatusIds) {
      params['categoryStatusIds'] = categoryStatusIds.join(',');
    }
    if (fullCategoryFlag != null) {
      params['fullCategoryFlag'] = fullCategoryFlag;
    }

    return this.http.get('/rest/portfolio', {
      headers: this.headers,
      params: params
    }).pipe(map(response => <Category[]>response));
  }

  getPhoto(photoId: number, size: SizeType = SizeType.BIG): Observable<Photo> {
    const params = { sizeType: size === SizeType.BIG ? 'BIG' : 'SMALL' };

    return this.http.get(`/rest/portfolio/getPhoto/${photoId}`, {
      headers: this.headers,
      params: params
    }).pipe(map(response => <Photo>response));
  }

  getBlogs(statusId: number[] = null,
    genreId: number[] = null,
    limit: number = null,
    offset: number = null):
    Observable<GetOverviewResponse<Blog>> {
    let params: HttpParams = new HttpParams();
    if (statusId) {
      params = params.append('statusId', statusId.toString());
    }
    if (genreId) {
      params = params.append('genreId', genreId.toString());
    }
    if (limit) {
      params = params.append('limit', limit.toString());
    }
    if (offset) {
      params = params.append('offset', offset.toString());
    }

    return this.http.get('/rest/blog/list', {
      headers: this.headers,
      params: params
    }).pipe(map(response => response as GetOverviewResponse<Blog>));
  }

  getBlogList(): Observable<any> {
    return this.http.get('/rest/blog/list', {
      headers: this.headers
    }).pipe(map(response => response as any));
  }

  getBlogDetailByUrl(blogUrl: string = null): Observable<BlogDetail> {
    return this.http.get(`/rest/blog/detail/${blogUrl}`, {
      headers: this.headers,
    }).pipe(map(response => response as BlogDetail));
  }

  getBase64BlogPhoto(blogPhotoId: number = null): Observable<FileObject> {
    return this.http.get(`/rest/blog/getPhoto/${blogPhotoId}`, {
      headers: this.headers,
    }).pipe(map(response => response as FileObject));
  }

  getSimpleAdsSurfaceByHashId(hash: string): Observable<SimpleAdsSurface[]> {
    return this.http.get(
      `${this.baseUrl}/adssurfaces/offer/${hash}`, {
        headers: this.headers,
      }).pipe(map(response => response as SimpleAdsSurface[]));
  }

  createRequirementFromOffer(hash: string, requirementFromWeb: RequirementFromWeb): Observable<boolean> {
    return this.http.post(
      `${this.baseUrl}/adssurfaces/createRequirementFromOffer/${hash}`,
      requirementFromWeb, {
        headers: this.headers,
      }).pipe(map(response => response as boolean));
  }

  getSellerDetailByOfferHashId(hash: string): Observable<UserDetail> {
    return this.http.get(
      `${this.baseUrl}/seller/${hash}`, {
        headers: this.headers,
      }).pipe(map(response => response as UserDetail));
  }
}
